import type { CityConfig } from "@/pages/city-template/types";

export const somertonConfig: CityConfig = {
  city: "Somerton",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "somerton",
  route: "/somerton",
  title: "Emergency & Expedited Passport Services Somerton AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Somerton, AZ — as fast as 24 hours. No Somerton passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 240 miles east. A+ BBB rated. Serving Yuma County agricultural workers, Cocopah Indian Tribe members, border community families, and cross-border professionals.",
  h1: "Emergency & Expedited Passport Services in Somerton, AZ",
  heroSubtitle: "Somerton sits in southwestern Yuma County, just minutes from the U.S.-Mexico border and about 240 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're an agricultural industry professional traveling for international training, a Cocopah community member with cross-border family connections, or a border region family heading on an international trip.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Somerton%20Arizona%20Yuma%20County%20agricultural%20landscape%20with%20date%20palm%20groves%20and%20citrus%20orchards%20under%20bright%20desert%20sun%2C%20small%20border%20community%20town%20with%20southwestern%20architecture%2C%20warm%20golden%20tones%20on%20irrigated%20farmland%20stretching%20to%20distant%20mountains%2C%20professional%20landscape%20photography%2C%20clean%20composition%2C%20no%20people%20visible&width=1800&height=700&seq=so-hero-001&orientation=landscape",
  heroImageAlt: "Somerton Arizona Yuma County agricultural landscape with date palms at golden hour",
  geoPosition: "32.5965;-114.7097",
  geoPlacename: "Somerton, Arizona",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 240 miles from Somerton — about 3.75 hours via I-8 and I-10",
  },
  nearbyCitiesZips: [
    { name: "Somerton", zips: "85350" },
    { name: "San Luis", zips: "85349, 85336" },
    { name: "Yuma", zips: "85364, 85365" },
    { name: "Gadsden", zips: "85336" },
    { name: "Fortuna Foothills", zips: "85367" },
    { name: "Wellton", zips: "85356" },
    { name: "Tacna", zips: "85352" },
    { name: "Dateland", zips: "85333" },
    { name: "Winterhaven CA", zips: "92283" },
    { name: "Bard CA", zips: "92222" },
    { name: "El Centro CA", zips: "92243" },
    { name: "Calexico CA", zips: "92231" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 240 miles from Somerton" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Yuma County Hub", desc: "Serving Somerton, San Luis, Gadsden, and all southwestern Yuma County communities" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Somerton case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Yuma AZ", route: "/yuma" },
    { name: "Expedited passport in Phoenix AZ", route: "/phoenix" },
    { name: "Urgent passport in Tucson AZ", route: "/tucson" },
    { name: "Arizona passport hub", route: "/arizona" },
    { name: "Fast passport in San Luis AZ", route: "/san-luis" },
    { name: "Emergency passport in Scottsdale AZ", route: "/scottsdale" },
    { name: "Expedited passport in Nogales AZ", route: "/nogales" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves Somerton remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Somerton is about 240 miles from Tucson — roughly 7.5 hours round trip through the Sonoran Desert along I-8. Our courier service eliminates this drive entirely, making it possible for border region residents to get their passports without sacrificing an entire workday to travel.",
  fpcOfficeDistance: "Serving all of southwestern Yuma County from the border region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Somerton, San Luis, Gadsden, and the Yuma County border corridor. From agricultural workers and Cocopah community members to border families with relatives in both the U.S. and Mexico, we help Southwest Arizona travelers get their passports fast without the 240-mile drive to Tucson.",
  nearbyCitiesIntro: "We serve Somerton and nearby communities including San Luis, Gadsden, Yuma, Fortuna Foothills, Wellton, Tacna, and the entire Yuma County border region — from the Cocopah Reservation to the Colorado River.",
  howItWorksIntro: "Most Somerton travelers complete everything online and ship documents overnight. At about 240 miles from Tucson along the I-8 corridor, Fast Passport Center provides essential courier turnaround for border region residents — a critical service for one of Arizona's most remote passport communities in the state's southwestern corner.",
  mistakesIntro: "Somerton travelers, including agricultural industry professionals and Cocopah community members, face unique passport challenges specific to border communities and the Yuma County agricultural corridor. Here are the most common mistakes we see.",
  testimonialsIntro: "Somerton and Yuma County border community travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Somerton? Here are the most common ones we hear from border community and agricultural industry travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "240 miles to Tucson (nearly 4-hour drive each way)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No 7.5-hour round-trip drive to Tucson needed",
        "Ideal for agricultural workers traveling internationally",
        "Perfect for Cocopah community member travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from YUM or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Somerton is 240 miles from Tucson. Don't waste a 7.5-hour round-trip desert drive — our couriers deliver to the Western Passport Center for you.",
      bullets: ["Southwest AZ coverage", "24–48 hour turnaround available", "No 7.5-hour Tucson drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Somerton adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Somerton and Yuma County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Somerton and Yuma County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Somerton, San Luis, and Cocopah community residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Yuma County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Somerton and border region residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Somerton AZ", description: "Urgent passport processing for Somerton, AZ travelers via Western Passport Center courier — about 240 miles from Somerton." },
    { name: "Passport Renewal Somerton AZ", description: "Fast passport renewal for Somerton and Yuma County, Arizona residents." },
    { name: "New Passport Somerton AZ", description: "First-time passport application assistance for Somerton and border region residents." },
    { name: "Lost Passport Somerton AZ", description: "Passport replacement for lost or stolen passports for Somerton and Southwest Arizona residents." },
    { name: "Child Passport Somerton AZ", description: "Minor passport services for Somerton and Yuma County families." },
    { name: "Name Change Passport Somerton AZ", description: "Passport name change service for Somerton, AZ residents." },
  ],
  testimonials: [
    {
      name: "Carlos R.",
      location: "Somerton, AZ",
      rating: 5,
      text: "I work in date palm cultivation for a major Yuma County agricultural operation. Our company sent me to a date industry conference in Saudi Arabia with 3 weeks notice. I'd never had a passport. Fast Passport Center guided me through the whole process and got my passport in 6 business days. The drive to Tucson would have taken an entire day — this service is essential for border region professionals.",
      service: "New Passport",
    },
    {
      name: "Maria S. and Family",
      location: "Somerton, AZ",
      rating: 5,
      text: "Cocopah community members with relatives in Baja California, Mexico. We needed passports for our whole family — three kids and two adults — for a family reunion. Fast Passport Center processed all five in 7 business days. The DS-11 guidance for the kids was invaluable, and not having to drive to Tucson saved us so much time.",
      service: "Family Passport Package",
    },
    {
      name: "Roberto V.",
      location: "Somerton, AZ",
      rating: 5,
      text: "Agricultural equipment technician who got offered a two-week training in Germany through our equipment manufacturer. Had 12 days to get a passport. Fast Passport Center got it done in 5 business days. The daily emails letting me know exactly where my application was — that was the best part. No stress, just results.",
      service: "Expedited Renewal",
    },
    {
      name: "Ana Laura G.",
      location: "Gadsden, AZ",
      rating: 5,
      text: "My sister in Mexicali was getting married and I was the maid of honor. Lost my passport during a move between Somerton and Gadsden — total panic. Fast Passport Center handled the DS-64 lost passport form and got me a replacement in 4 business days. I made it to the wedding. Cannot thank them enough.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "so-1",
      name: "Somerton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "102 W Main St",
      city: "Somerton",
      state: "AZ",
      zip: "85350",
      phone: "(928) 627-8520",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Somerton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Somerton, Gadsden, and Cocopah community residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "so-2",
      name: "San Luis Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "874 N Main St",
      city: "San Luis",
      state: "AZ",
      zip: "85349",
      phone: "(928) 627-2034",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles south in San Luis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Somerton, San Luis, and border community residents.",
      icon: "ri-mail-line",
    },
    {
      id: "so-3",
      name: "Yuma Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2222 S 4th Ave",
      city: "Yuma",
      state: "AZ",
      zip: "85364",
      phone: "(928) 343-2796",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "20 miles north in Yuma",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest acceptance facility in Yuma County with most appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "so-4",
      name: "Cocopah Tribal Administration Office",
      type: "Tribal Passport Acceptance",
      address: "14515 S Veterans Dr",
      city: "Somerton",
      state: "AZ",
      zip: "85350",
      phone: "(928) 627-2102",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM (passport services — call ahead)",
      distance: "Somerton",
      website: "https://www.cocopah.com/",
      websiteLabel: "Cocopah Indian Tribe",
      notes: "Limited passport acceptance services available for Cocopah community members. Call ahead to confirm passport service hours.",
      icon: "ri-building-line",
    },
    {
      id: "so-5",
      name: "Arizona Western College — San Luis Learning Center",
      type: "Educational Institution Passport Acceptance",
      address: "1340 N 8th Ave",
      city: "San Luis",
      state: "AZ",
      zip: "85349",
      phone: "(928) 627-9563",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM (limited passport hours — call ahead)",
      distance: "10 miles south in San Luis",
      website: "https://www.azwestern.edu/san-luis",
      websiteLabel: "AWC San Luis",
      notes: "Limited passport acceptance services available. Call ahead to confirm hours. Convenient for Somerton residents visiting the San Luis area.",
      icon: "ri-building-line",
    },
    {
      id: "so-6",
      name: "Fortuna Foothills Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11681 S Fortuna Rd",
      city: "Yuma",
      state: "AZ",
      zip: "85367",
      phone: "(928) 342-3635",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles northeast in Fortuna Foothills",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern Yuma County and Fortuna Foothills residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "240 miles — nearly 4-hr drive each way through desert", fpc: "We drive for you — no 7.5-hour desert round trip needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking You Can Drive to Tucson in a Day",
      description: "Somerton is 240 miles from Tucson — nearly 4 hours each way through the Sonoran Desert along I-8. That is a 7.5-hour round trip just to reach the Western Passport Center — and you still need an appointment. Our courier service eliminates this entirely. Do not waste a full workday driving across the desert.",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Card vs. Passport Book — Border Community Confusion",
      description: "Many Somerton residents use passport cards for land border crossings into Mexico at the San Luis port of entry. But passport cards do not work for air travel — not even domestic flights within Mexico. If you fly from Mexicali to Guadalajara or Mexico City, you need a full passport book. Many border residents learn this the hard way at the airport.",
    },
    {
      icon: "ri-sun-line",
      title: "Summer Heat and Document Safety",
      description: "Somerton experiences extreme summer temperatures regularly exceeding 110°F from May through September. Standard USPS mail can be unreliable during extreme heat months. Fast Passport Center's overnight courier with temperature-controlled handling and tracking is far more reliable than standard mail for Yuma County's intense desert climate.",
    },
    {
      icon: "ri-file-list-line",
      title: "Agricultural Workers — H-2A and Documentation Requirements",
      description: "Yuma County agricultural professionals on H-2A visas have specific documentation requirements beyond standard passport applications. First-time U.S. passport applicants need certified birth certificates — not photocopies, not hospital certificates. Ordering certified copies from out of state can take weeks.",
    },
    {
      icon: "ri-parent-line",
      title: "Cross-Border Families and Minor Documentation",
      description: "Somerton families with children needing first passports must have both parents appear in person — or one parent with notarized Form DS-3053. If one parent works or lives in Mexico, they can get Form DS-3053 notarized at a U.S. consulate. Start this process 2–3 weeks early to coordinate across the border.",
    },
    {
      icon: "ri-image-line",
      title: "Border Region Photo Quality Inconsistency",
      description: "Passport photo services in border communities can be inconsistent in quality. Photos must be exactly 2x2 inches with a white background, no shadows, neutral expression, and no glasses. Always verify your photos meet U.S. Department of State standards — improper photos are the number one cause of application rejection.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Somerton, San Luis, or Yuma." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — 240 miles east via I-8." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Somerton or Yuma County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Somerton from a passport agency?",
      a: "Somerton is approximately 240 miles from the Western Passport Center in Tucson — about a 3.75-hour drive each way via I-8 and I-10 through the Sonoran Desert. That is a 7.5-hour round trip. Somerton is in one of Arizona's most remote corners, making Fast Passport Center's courier service essential for border region travelers who cannot afford to lose an entire workday.",
    },
    {
      q: "Does Somerton have its own passport acceptance facility?",
      a: "Yes. The Somerton Post Office at 102 W Main Street is a USPS passport acceptance facility. The Cocopah Tribal Administration Office also offers limited passport acceptance services for community members. Both require appointments and operate during regular business hours on weekdays.",
    },
    {
      q: "How fast can I get an emergency passport in Somerton?",
      a: "Emergency processing is typically 24–48 hours for Somerton residents. Our couriers deliver directly to the Western Passport Center in Tucson — 240 miles away. This saves you a 7.5-hour desert round-trip drive. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Can I fly from Yuma International Airport internationally?",
      a: "Yuma International Airport (YUM) primarily serves domestic routes and military operations. For international commercial flights, Somerton residents typically use Phoenix Sky Harbor International Airport (PHX, about 180 miles northeast) or San Diego International Airport (SAN, about 165 miles west in California). Both require a valid passport book.",
    },
    {
      q: "I'm an agricultural worker — can I apply for a passport by mail?",
      a: "If you have never had a U.S. passport before, you must apply in person using Form DS-11 at an acceptance facility like the Somerton Post Office. Adult renewals (Form DS-82) can be done by mail. Fast Passport Center can help with both — our courier service handles the fast turnaround after your acceptance facility visit.",
    },
    {
      q: "How do I renew my passport in Somerton?",
      a: "Adult renewals (16 or older, issued within 15 years, same name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks). This is especially valuable for Somerton residents given the 240-mile distance to Tucson.",
    },
    {
      q: "Where can I get passport photos in Somerton?",
      a: "Passport photo options in Somerton are limited. The closest reliable locations are Walgreens at 2800 S 4th Avenue in Yuma (20 miles north), CVS Pharmacy at 1600 S 4th Avenue in Yuma, and Walmart Photo Center at 1640 N Main Street in San Luis (10 miles south). Always verify your photos meet exact 2x2 inch specifications.",
    },
    {
      q: "Do you serve Cocopah community members?",
      a: "Yes. We serve all residents of Somerton and the surrounding area including Cocopah Indian Tribe members. The Cocopah Tribal Administration Office offers passport acceptance services for community members. Fast Passport Center provides the courier and fast processing component once your application is complete.",
    },
    {
      q: "What if I need passports for my children and one parent is in Mexico?",
      a: "Somerton border families where one parent is in Mexico can still get children's passports. The absent parent must complete and notarize Form DS-3053 (Statement of Consent) at a U.S. consulate in Mexico and send the original to you. Start this process at least 3–4 weeks before your planned travel — cross-border coordination takes time.",
    },
    {
      q: "How does summer heat affect passport document shipping from Somerton?",
      a: "Somerton is in one of the hottest regions of Arizona, with summer temperatures regularly above 110°F. Standard USPS mail delivery can be unreliable during extreme heat. Fast Passport Center uses UPS overnight courier with full tracking and temperature-controlled handling to ensure your documents arrive safely in Tucson, even during Yuma County's most brutal summer days.",
    },
    {
      q: "Can I use my Mexican passport if I'm a dual citizen?",
      a: "U.S. citizens, including dual citizens holding both U.S. and Mexican citizenship, must enter and depart the United States using a valid U.S. passport. Even if you hold Mexican citizenship, you need a valid U.S. passport for international air travel to and from the United States.",
    },
    {
      q: "Do you serve San Luis and Gadsden residents too?",
      a: "Yes. We serve all of Yuma County including San Luis, Gadsden, Fortuna Foothills, Wellton, Tacna, and the entire border corridor. Documents ship overnight from any UPS location in the Somerton, San Luis, and Yuma area.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Yuma (4th Ave)", address: "2800 S 4th Ave, Yuma, AZ 85364", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Yuma (4th Ave)", address: "1600 S 4th Ave, Yuma, AZ 85364", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Yuma", address: "2450 S 4th Ave, Ste 107, Yuma, AZ 85364", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
    { name: "Walmart Photo Center — San Luis", address: "1640 N Main St, San Luis, AZ 85349", hours: "Mon–Sun: 8AM–8PM" },
    { name: "Walgreens — Yuma (16th St)", address: "2491 W 16th St, Yuma, AZ 85364", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Yuma", address: "1450 S Yuma Palms Pkwy, Yuma, AZ 85365", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Somerton", address: "451 E Main St, Somerton, AZ 85350", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Somerton, Gadsden, and Cocopah community residents. Right in town on Main Street.", featured: true },
    { name: "AZ MVD — San Luis", address: "815 N Cesar Chavez Blvd, San Luis, AZ 85349", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves San Luis and southern border community residents. About 5 miles south.", featured: false },
    { name: "AZ MVD — Yuma", address: "2165 E Gila Ridge Rd, Yuma, AZ 85365", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves greater Yuma area residents. About 20 miles north.", featured: false },
  ],
  airports: [
    {
      code: "YUM",
      name: "Yuma International Airport",
      distance: "20 miles north of Somerton",
      address: "2191 E 32nd St, Yuma, AZ 85365",
      note: "Closest airport to Somerton with American Airlines domestic service to Phoenix and Dallas. Limited to domestic routes — international travelers connect through PHX. Passport required for international connections.",
      flightUrl: "https://www.yumaairport.com/",
      flightLabel: "YUM Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "180 miles northeast of Somerton",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 3 hours via I-8 and I-10.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "165 miles west of Somerton",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "Major California international hub with extensive Pacific and Latin American routes. About 2.5 hours west via I-8. Often the best international option for Yuma County travelers heading to Mexico and Asia.",
      flightUrl: "https://www.san.org/",
      flightLabel: "SAN Airport",
      featured: false,
    },
  ],
};